import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  TotpMultiFactorGenerator,
  multiFactor,
} from "firebase/auth";
import { QRCodeSVG } from "qrcode.react";
import { auth } from "../firebase";
import AuthLayout from "./auth/AuthLayout";
import OtpCodeInput from "./auth/OtpCodeInput";

const Form = styled.form`
  display: grid;
  gap: 0.85rem;
`;

const ErrorMessage = styled.div`
  border: 1px solid #fecaca;
  background: #fff7f7;
  color: #b91c1c;
  border-radius: 10px;
  padding: 0.56rem 0.65rem;
  font-size: 0.8rem;
`;

const InfoMessage = styled.p`
  margin: -0.15rem 0 0.2rem;
  color: #64748b;
  font-size: 0.84rem;
  line-height: 1.5;
`;

const QrPanel = styled.div`
  display: grid;
  justify-items: center;
  gap: 0.78rem;
  padding: 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
`;

const ManualKey = styled.code`
  display: block;
  width: 100%;
  word-break: break-all;
  padding: 0.52rem 0.58rem;
  border-radius: 8px;
  border: 1px solid #dbe3ee;
  background: #ffffff;
  color: #334155;
  font-size: 0.74rem;
`;

const PrimaryButton = styled.button`
  height: 2.9rem;
  border: 1px solid #2f435f;
  border-radius: 10px;
  background: #324966;
  color: #ffffff;
  font-size: 0.92rem;
  font-weight: 650;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  transition: transform 150ms ease-in-out, background-color 150ms ease-in-out, border-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.14), 0 6px 16px rgba(15, 23, 42, 0.16);

  &:hover {
    transform: translateY(-1px);
    background: #2a3d57;
    border-color: #2a3d57;
    box-shadow: inset 0 1px rgba(255, 255, 255, 0.12), 0 10px 20px rgba(15, 23, 42, 0.18);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.24);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  height: 2.6rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out;

  &:hover {
    background: #f1f5f9;
    border-color: #9ca3af;
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.24);
  }
`;

const ButtonStack = styled.div`
  display: grid;
  gap: 0.58rem;
`;

const SpinnerIcon = styled(Loader2)`
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default function EnrollMfa() {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [sharedKey, setSharedKey] = useState("");
  const [secret, setSecret] = useState<any>(null);
  const [error, setError] = useState("");
  const [isLoadingSetup, setIsLoadingSetup] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const user = auth.currentUser;

  const generateSecret = useCallback(async () => {
    if (!user) return;

    setIsLoadingSetup(true);
    setError("");

    try {
      const session = await multiFactor(user).getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);

      setSecret(totpSecret);
      setSharedKey(totpSecret.secretKey);
      setQrCodeUrl(totpSecret.generateQrCodeUrl(user.email || "User", "MedCoderAI"));
    } catch (setupError: any) {
      if (setupError?.code === "auth/operation-not-allowed") {
        setError(
          "TOTP multi-factor authentication is not enabled for this project. " +
          "A Firebase admin must enable it under Authentication → Sign-in method → Multi-factor authentication in the Firebase Console."
        );
      } else {
        setError(`Unable to prepare MFA setup. ${setupError.message || "Please retry."}`);
      }
    } finally {
      setIsLoadingSetup(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    void generateSecret();
  }, [generateSecret, navigate, user]);

  const handleEnroll = useCallback(
    async (overrideCode?: string) => {
      if (!secret || !user || isSubmitting) return;

      const code = (overrideCode || otp.join("")).trim();
      if (code.length !== 6) {
        setError("Enter the full 6-digit code from your authenticator app.");
        return;
      }

      setIsSubmitting(true);
      setError("");

      try {
        const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
          secret,
          code
        );

        await multiFactor(user).enroll(multiFactorAssertion, "Authenticator App");
        navigate("/auto-claims");
      } catch (enrollError: any) {
        console.error("MFA enrollment failed:", enrollError);
        if (enrollError?.code === "auth/requires-recent-login") {
          setError("Your session has expired. Please log out and log back in, then try enrolling again right away.");
        } else if (enrollError?.code === "auth/invalid-verification-code") {
          setError("That code doesn't match. Make sure your phone's time is set automatically, then enter the current code without refreshing this page.");
        } else {
          setError(`Enrollment failed: ${enrollError?.code || enrollError?.message || "unknown error"}. Wait for a fresh code and try again.`);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, navigate, otp, secret, user]
  );

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleEnroll();
  };

  return (
    <AuthLayout
      cardTitle="Set up multi-factor authentication"
      cardSubtitle="Scan the QR code in your authenticator app, then verify with a 6-digit code."
    >
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      {isLoadingSetup ? (
        <InfoMessage>Generating secure setup credentials...</InfoMessage>
      ) : qrCodeUrl ? (
        <Form onSubmit={onSubmit} noValidate>
          <QrPanel>
            <InfoMessage style={{ margin: 0, textAlign: "center" }}>
              Scan this QR code with Google Authenticator, Microsoft Authenticator, or any TOTP app.
            </InfoMessage>
            <div style={{ background: "#ffffff", padding: "0.55rem", borderRadius: "10px" }}>
              <QRCodeSVG value={qrCodeUrl} size={158} />
            </div>
            <div style={{ width: "100%" }}>
              <InfoMessage style={{ marginBottom: "0.32rem" }}>Manual setup key</InfoMessage>
              <ManualKey>{sharedKey || "-"}</ManualKey>
            </div>
          </QrPanel>

          <InfoMessage>
            Enter the current 6-digit code from your authenticator app.
          </InfoMessage>

          <OtpCodeInput
            value={otp}
            onChange={setOtp}
            onSubmit={(code) => {
              if (!isSubmitting) {
                void handleEnroll(code);
              }
            }}
            disabled={isSubmitting}
            autoFocus
          />

          <ButtonStack>
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? <SpinnerIcon size={16} /> : null}
              {isSubmitting ? "Activating..." : "Activate MFA"}
            </PrimaryButton>
            <SecondaryButton
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                setOtp(new Array(6).fill(""));
                void generateSecret();
              }}
            >
              Regenerate setup
            </SecondaryButton>
          </ButtonStack>
        </Form>
      ) : null}
    </AuthLayout>
  );
}
